import { useState } from 'react'
import { Alert, Button, Card, Descriptions, Table, Tag, Tooltip, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { EmptyState } from '@/shared/ui/EmptyState'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { PermissionNotice } from '@/shared/ui/PermissionNotice'
import { actorLabel } from '@/features/identity/actorName'
import { useActorDirectory } from '@/features/identity/useActorDirectory'
import { BankAccountModal } from './BankAccountModal'
import { currentAccount, fetchBankAccounts, type BankAccount } from './bankApi'

interface Props {
  personId: string
}

/**
 * Bank rekvizitlari — joriysi va butun tarixi.
 *
 * <h2>⚠️ HUQUQSIZ SO'ROV YUBORILMAYDI</h2>
 * `hr.bank.view` — `Permission.DANGEROUS` ro'yxatida va hech bir tizim
 * roliga o'z-o'zidan tushmaydi. `SalaryCard` dagi bilan aynan bir xil
 * qoida: `enabled`, niqob YO'Q, o'rniga sabab va huquq KODI.
 *
 * <h2>⚠️ RAQAM NIQOBLANMAYDI</h2>
 * Sabab `bankApi.ts` da yozilgan: operator raqamni bankka o'tkazadi va
 * harfma-harf solishtiradi, niqob esa aynan shu ishni to'sardi. Himoya
 * huquqda, ko'rinishda emas.
 */
export function BankAccountsCard({ personId }: Props) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const canView = can('hr.bank.view')
  const canWrite = can('hr.bank.write')

  // ⚠️ N4: `bank_account.changed_by` — `auth.getName()`, aktyor
  //    identifikatori. Nom `actorName.ts` xaritasidan keladi.
  const actors = useActorDirectory()

  const [open, setOpen] = useState(false)

  const accounts = useQuery({
    queryKey: queryKeys.hr.bankAccounts(personId),
    queryFn: () => fetchBankAccounts(personId),
    enabled: canView && personId !== '',
  })

  if (!canView) {
    return (
      <Card title={t('hr.bank.title')}>
        <PermissionNotice
          permission="hr.bank.view"
          title={t('hr.bank.hiddenTitle')}
          reason={t('hr.bank.hiddenReason')}
        />
      </Card>
    )
  }

  const rows = accounts.data ?? []
  const active = currentAccount(rows)

  const columns: ColumnsType<BankAccount> = [
    { title: t('hr.bank.bankName'), dataIndex: 'bankName', key: 'bankName' },
    {
      title: t('hr.bank.accountNo'),
      key: 'accountNo',
      render: (_value, row) => (
        <Typography.Text copyable={{ text: row.accountNo }}>{row.accountNo}</Typography.Text>
      ),
    },
    {
      title: t('hr.bank.holderName'),
      key: 'holderName',
      render: (_value, row) => row.holderName ?? t('hr.bank.holderSelf'),
    },
    { title: t('hr.bank.validFrom'), dataIndex: 'validFrom', key: 'validFrom' },
    {
      title: t('hr.bank.validTo'),
      key: 'validTo',
      render: (_value, row) =>
        row.open ? <Tag color="green">{t('hr.bank.current')}</Tag> : (row.validTo ?? '—'),
    },
    {
      title: t('hr.bank.reason'),
      key: 'reason',
      render: (_value, row) =>
        row.reason === null ? '—' : <ExpandableHint text={row.reason} />,
    },
    {
      title: t('hr.salary.changedBy'),
      key: 'changedBy',
      render: (_value, row) => actorLabel(row.changedBy, actors, t('common.dash')),
    },
    {
      title: t('common.createdAt'),
      key: 'createdAt',
      render: (_value, row) =>
        row.createdAt === null ? (
          '—'
        ) : (
          <Tooltip title={utcTooltip(row.createdAt)}>
            <span>{formatDateTime(row.createdAt)}</span>
          </Tooltip>
        ),
    },
  ]

  return (
    <Card
      title={t('hr.bank.title')}
      extra={
        canWrite ? <Button onClick={() => setOpen(true)}>{t('hr.bank.setAction')}</Button> : null
      }
    >
      {accounts.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(accounts.error, t)}
        />
      ) : null}

      {active === null ? null : (
        <Descriptions column={{ xs: 1, md: 2 }} size="small" bordered style={{ marginBottom: 16 }}>
          <Descriptions.Item label={t('hr.bank.bankName')}>{active.bankName}</Descriptions.Item>
          <Descriptions.Item label={t('hr.bank.accountNo')}>
            <Typography.Text copyable={{ text: active.accountNo }}>
              {active.accountNo}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label={t('hr.bank.holderName')}>
            {active.holderName ?? t('hr.bank.holderSelf')}
          </Descriptions.Item>
          <Descriptions.Item label={t('hr.bank.validFrom')}>{active.validFrom}</Descriptions.Item>
        </Descriptions>
      )}

      {/*
        ⚠️ «Rekvizit yo'q» xulosasi FAQAT `isSuccess` da chiqadi
        (`guards.mjs` 3-bo'limi): yiqilgan so'rovda ro'yxat ham bo'sh
        bo'ladi va bo'sh holat «bu odamda hisob yo'q» degan ISHONCHLI
        javob bo'lib ko'rinardi.
      */}
      {accounts.isSuccess && rows.length === 0 ? (
        <EmptyState title={t('hr.bank.empty')} description={t('hr.bank.emptyHint')} />
      ) : (
        <Table<BankAccount>
          rowKey={(row) => row.id}
          columns={columns}
          dataSource={[...rows]}
          loading={accounts.isPending}
          pagination={false}
          size="small"
          scroll={{ x: 900 }}
        />
      )}

      <BankAccountModal
        open={open}
        personId={personId}
        current={active}
        onClose={() => setOpen(false)}
      />
    </Card>
  )
}
